import { type CommentingOptions, generateCommentedDocx } from '@/api';
import { downloadBlob } from '@/utils/download';

/**
 * The commented-Word export: the source document returned with the review's
 * findings attached as tracked comments.
 *
 * Goes through the generated client. The previous copy bypassed it with a raw
 * `fetch` against `VITE_API_URL` and a hand-fetched bearer token — the endpoint
 * has been in the SDK all along, and going through it keeps auth, base URL and
 * error shape consistent with every other call.
 */

/** `Report_v3.docx` → `Report_v3_commented.docx`. */
function commentedFileName(originalFileName: string): string {
  return `${originalFileName.replace(/\.docx?$/i, '')}_commented.docx`;
}

/**
 * Generate a DOCX of the source document with the review's findings as tracked
 * comments, and hand it to the browser.
 */
export async function downloadCommentedDocx(
  reviewUid: string,
  fileReviewUid: string,
  originalFileName: string,
  options: CommentingOptions
): Promise<void> {
  const response = await generateCommentedDocx({
    path: { review_uid: reviewUid, file_review_uid: fileReviewUid },
    body: options,
    parseAs: 'blob',
    throwOnError: true,
  });

  const blob = response.data;
  if (!(blob instanceof Blob)) {
    throw new TypeError('Commented DOCX export did not return a file');
  }

  downloadBlob(blob, commentedFileName(originalFileName));
}
